import React from 'react';
import { Callout, Country, Loading, SectionedList } from 'components/atoms';
import { useFetch } from 'hooks';
import { useConfig } from 'config';
import { useAuth } from 'auth';
import { RelatedThirdPartyDetailsLoadingContainer } from '../ThirdParty.styles';
import { Indicator } from 'components/molecules';
import { ThirdPartyType } from 'types';
import { useTerminologyConfig } from 'context/DataContext';
import { ThirdPartyLinkageDetailsProps } from './types';

const ThirdPartyLinkageDetails: React.FC<ThirdPartyLinkageDetailsProps> = ({
  id,
  relatedThirdPartiesCount,
}) => {
  const { vantageWebApi } = useConfig();
  const { tokenInfo } = useAuth();
  const terminologyConfig = useTerminologyConfig();

  const getThirdPartyIcon = (type) => {
    switch (type) {
      case ThirdPartyType.Individual:
        return 'user';
      case ThirdPartyType.Business:
        return 'company';
      default:
        return 'unknown';
    }
  };

  const {
    data: relatedThirdPartyDetails,
    loading: relatedThirdPartyLoading,
    trigger: fetchRelatedThirdPartyDetails,
  } = useFetch(`${vantageWebApi}/thirdParty/:id/linkages`, {
    method: 'GET',
    lazy: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
  });

  const handleRelatedThirdPartyCalloutOpen = (id: string, relatedThirdPartiesCount: number) => {
    if (relatedThirdPartiesCount === 0) {
      return;
    }
    fetchRelatedThirdPartyDetails({
      urlParams: { id },
    });
  };

  return (
    <Callout
      name={`Related third parties: ${relatedThirdPartiesCount}`}
      direction="right"
      trigger={() => (
        <span
          className="highlight"
          onClick={() => handleRelatedThirdPartyCalloutOpen(id, relatedThirdPartiesCount)}
        >
          {relatedThirdPartiesCount}
        </span>
      )}
      maxHeight="300px"
      width={relatedThirdPartiesCount > 0 && !relatedThirdPartyLoading ? '302px' : null}
    >
      {relatedThirdPartiesCount > 0 ? (
        <>
          {relatedThirdPartyLoading && (
            <RelatedThirdPartyDetailsLoadingContainer>
              <Loading />
            </RelatedThirdPartyDetailsLoadingContainer>
          )}
          {!relatedThirdPartyLoading && relatedThirdPartyDetails != null && (
            <>
              <SectionedList
                sections={relatedThirdPartyDetails.map(
                  ({ linkType, name, type, primaryCountry, isVisibleForCurrentUser }) => ({
                    heading: isVisibleForCurrentUser ? name : '*****',
                    items: [
                      {
                        key: 'Entity type',
                        value: isVisibleForCurrentUser ? (
                          <>
                            <Indicator name={name} icon={getThirdPartyIcon(type)} />
                            <span style={{ marginLeft: '0.5rem' }}>
                              {type === 'Business' ? 'Company' : type}
                            </span>
                          </>
                        ) : (
                          '*****'
                        ),
                      },
                      {
                        key: 'Country',
                        value: isVisibleForCurrentUser ? (
                          <>
                            {primaryCountry && <Country countryCode={primaryCountry} fullWidth />}
                          </>
                        ) : (
                          '**'
                        ),
                      },
                      {
                        key: 'Relationship',
                        value: isVisibleForCurrentUser ? linkType : '*****',
                      },
                    ],
                  })
                )}
                cta={{
                  label: 'View All',
                  onClick: () => {},
                  href: `/third-party/profile/${id}`,
                }}
              />
            </>
          )}
        </>
      ) : (
        <p style={{ padding: '0.652rem' }}>No Linked {terminologyConfig?.thirdPartyPluralNaming}</p>
      )}
    </Callout>
  );
};

export default ThirdPartyLinkageDetails;
